'use client'

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

// Depth-aware nested-modal stacking: DialogContent raises overlay/content z-index
// per nesting level (context propagates through portals) so 3+ deep modals stack
// correctly (parent dims, child on top). Was a static z-50.
const DialogDepthContext = React.createContext(0)
const DIALOG_Z_BASE = 50
const DIALOG_Z_STEP = 10

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, style, ...props }, ref) => {
  const parentDepth = React.useContext(DialogDepthContext)
  const z = DIALOG_Z_BASE + parentDepth * DIALOG_Z_STEP
  return (
  <DialogPortal>
    <DialogOverlay style={{ zIndex: z }} />
    <DialogPrimitive.Content
      ref={ref}
      style={{ zIndex: z + 1, ...style }}
      className={cn(
        // w-[calc(100%-1.5rem)] keeps a 12px gutter on 320/375px phones so the
        // modal never touches the screen edges; max-w-lg caps it on larger screens.
        'fixed left-[50%] top-[50%] grid w-[calc(100%-1.5rem)] max-w-lg translate-x-[-50%] translate-y-[-50%]',
        // 3D elevated design
        'border-[1.5px] border-[hsl(var(--modal-border))] bg-[hsl(var(--modal-bg))] text-[hsl(var(--modal-fg))] rounded-2xl',
        'shadow-[0_25px_60px_-12px_rgba(0,0,0,0.35),0_0_0_1px_rgba(255,255,255,0.05)_inset,0_1px_2px_rgba(255,255,255,0.1)_inset]',
        'dark:shadow-[0_25px_60px_-12px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.05)_inset]',
        // Animation
        'duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        // R4 SYSTEMIC: cap height + scroll so a tall modal never clips its own
        // footer (Submit/Save) on short viewports. Actions are always reachable.
        'max-h-[90dvh] overflow-y-auto overflow-x-hidden',
        className
      )}
      {...props}
    >
      <DialogDepthContext.Provider value={parentDepth + 1}>
        {children}
      </DialogDepthContext.Provider>
      {/* 3D Close button */}
      <DialogPrimitive.Close className={cn(
        'absolute right-4 top-4 z-10',
        'h-7 w-7 rounded-lg flex items-center justify-center',
        'bg-black/5 dark:bg-white/10 backdrop-blur-sm',
        'opacity-70 transition-all hover:opacity-100 hover:bg-black/10 dark:hover:bg-white/20',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
        'disabled:pointer-events-none'
      )}>
        <X className="h-3.5 w-3.5" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

// Gradient variants for 3D header
const GRADIENT_VARIANTS = {
  emerald: 'from-emerald-500 via-emerald-600 to-emerald-700',
  amber: 'from-amber-500 via-amber-600 to-amber-700',
  red: 'from-red-500 via-red-600 to-red-700',
  blue: 'from-blue-500 via-blue-600 to-blue-700',
  indigo: 'from-indigo-500 via-indigo-600 to-indigo-700',
  purple: 'from-purple-500 via-purple-600 to-purple-700',
  cyan: 'from-cyan-500 via-cyan-600 to-cyan-700',
  rose: 'from-rose-500 via-rose-600 to-rose-700',
  teal: 'from-teal-500 via-teal-600 to-teal-700',
  orange: 'from-orange-500 via-orange-600 to-orange-700',
  slate: 'from-slate-600 via-slate-700 to-slate-800',
} as const

type GradientVariant = keyof typeof GRADIENT_VARIANTS

interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  gradient?: GradientVariant | 'none'
  icon?: React.ElementType
}

const DialogHeader = ({
  className,
  gradient,
  icon: Icon,
  children,
  ...props
}: DialogHeaderProps) => {
  // Modern default: every header gets a gradient strip unless explicitly opted out via gradient="none"
  const resolved: GradientVariant | 'none' = gradient ?? 'indigo'
  if (resolved !== 'none') {
    const gradientClass = GRADIENT_VARIANTS[resolved]
    return (
      <div
        className={cn(
          'relative overflow-hidden rounded-t-2xl -mx-0 -mt-0',
          className
        )}
        {...props}
      >
        {/* 3D Gradient background */}
        <div className={cn(
          'bg-gradient-to-br text-white px-6 py-5',
          gradientClass,
          // Radial 3D depth effect
          'before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_20%_20%,rgba(255,255,255,0.15)_0%,transparent_60%)]',
          'relative'
        )}>
          <div className="flex items-center gap-3 relative z-10">
            {Icon && (
              <div className="h-10 w-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20 shadow-lg">
                <Icon className="h-5 w-5 text-white" />
              </div>
            )}
            <div className="flex flex-col space-y-1 text-left [&_*]:text-white">
              {children}
            </div>
          </div>
        </div>
        {/* Bottom edge highlight */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
      </div>
    )
  }

  // Opt-out: plain header with border-bottom for visual separation
  return (
    <div
      className={cn(
        'flex flex-col space-y-1.5 text-left px-6 pt-6 pb-4 border-b border-border/60',
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      // 3D gradient footer
      'px-6 py-4 bg-gradient-to-t from-muted/50 to-transparent',
      'border-t border-border/50',
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('px-6 py-4', className)}
    {...props}
  />
)
DialogBody.displayName = 'DialogBody'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogBody,
  DialogTitle,
  DialogDescription,
}

export type { GradientVariant }
